import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert } from './ui/alert';

interface ValidationResult {
  isValid: boolean;
  error: string;
}

interface Tag {
  value: string;
  isValid: boolean;
  error: string;
}

interface EmailRecipientInputProps {
  recipients: Tag[];
  onRecipientsChange: (recipients: Tag[]) => void;
  validateRecipient: (value: string) => ValidationResult;
  formatRecipient: (value: string) => string;
  placeholder?: string;
  separator?: RegExp;
  maxRecipients?: number;
}

export function EmailRecipientInput({
  recipients,
  onRecipientsChange,
  validateRecipient,
  formatRecipient,
  placeholder = 'Add one or multiple emails',
  separator = /[;,\n]+/,
  maxRecipients,
}: EmailRecipientInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [duplicateWarning, setDuplicateWarning] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState(50);

  const hasReachedLimit = maxRecipients !== undefined && recipients.length >= maxRecipients;

  useEffect(() => {
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }, [recipients]);

  useEffect(() => {
    if (spanRef.current) {
      const width = spanRef.current.offsetWidth + 20;
      setInputWidth(Math.max(width, 200));
    }
  }, [editingValue]);

  useEffect(() => {
    if (!inputValue) setDuplicateWarning('');
  }, [inputValue]);

  useEffect(() => {
    if (spanRef.current) {
      const width = spanRef.current.offsetWidth + 20;
      setInputWidth(Math.max(width, 200));
    }
  }, [inputValue, editingValue]);

  const isDuplicate = (value: string, existing = recipients) =>
    existing.some(r => r.value.toLowerCase() === value.toLowerCase());

  const parseValues = (text: string) =>
    text
      .split(separator)
      .map(formatRecipient)
      .map(s => s.trim())
      .filter(Boolean);

  const addRecipientsFromText = (text: string) => {
    if (hasReachedLimit) return;

    const values = parseValues(text);
    const newRecipients: Tag[] = [];
    const duplicates: string[] = [];
    const seen = new Set<string>();

    for (const val of values) {
      const lower = val.toLowerCase();
      if (isDuplicate(val) || seen.has(lower)) {
        duplicates.push(val);
        continue;
      }
      seen.add(lower);
      const validation = validateRecipient(val);
      newRecipients.push({ value: val, isValid: validation.isValid, error: validation.error });
    }

    if (duplicates.length)
      setDuplicateWarning(`${[...new Set(duplicates)].join(', ')} already added`);
    else setDuplicateWarning('');

    const available = maxRecipients ? maxRecipients - recipients.length : newRecipients.length;
    const limited = newRecipients.slice(0, available);

    onRecipientsChange([...recipients, ...limited]);
  };

  const removeRecipient = (value: string) => {
    onRecipientsChange(recipients.filter(r => r.value !== value));
    setFocusedIndex(-1);
    setEditingIndex(null);
    setEditingValue('');
    setDuplicateWarning('');
  };

  const commitEditingChip = () => {
    if (editingIndex === null) return;
    const val = formatRecipient(editingValue).trim();

    const next = [...recipients];

    if (!val) {
      // Remove recipient if editing is cleared
      next.splice(editingIndex, 1);
    } else {
      const dup = next.some(
        (r, i) => i !== editingIndex && r.value.toLowerCase() === val.toLowerCase(),
      );
      if (dup) {
        setDuplicateWarning(`${val} already added`);
        setEditingIndex(null);
        setEditingValue('');
        return;
      }
      const validation = validateRecipient(val);
      next[editingIndex] = { value: val, isValid: validation.isValid, error: validation.error };
    }

    onRecipientsChange(next);
    setEditingIndex(null);
    setEditingValue('');
    setFocusedIndex(-1);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;

    if (['Enter', 'Tab', ',', ';', ' '].includes(key) && inputValue.trim()) {
      e.preventDefault();
      addRecipientsFromText(inputValue);
      setInputValue('');
      return;
    }

    if (key === 'Backspace' && inputValue === '' && recipients.length > 0) {
      if (focusedIndex === -1) setFocusedIndex(recipients.length - 1);
      else removeRecipient(recipients[focusedIndex].value);
      return;
    }

    if (key === 'ArrowLeft')
      setFocusedIndex(prev => (prev === -1 ? recipients.length - 1 : prev - 1));
    if (key === 'ArrowRight')
      setFocusedIndex(prev => (prev === recipients.length - 1 ? -1 : prev + 1));
  };

  const handleChipKeyDown = (e: KeyboardEvent<HTMLDivElement>, idx: number) => {
    const key = e.key;

    if (['Backspace', 'Delete'].includes(key)) {
      e.preventDefault();
      removeRecipient(recipients[idx].value);
      setFocusedIndex(idx > 0 ? idx - 1 : -1);
      return;
    }

    if (key === 'ArrowLeft') setFocusedIndex(idx > 0 ? idx - 1 : 0);
    if (key === 'ArrowRight') setFocusedIndex(idx < recipients.length - 1 ? idx + 1 : -1);
    if (key === 'Enter') {
      e.preventDefault();
      setEditingIndex(idx);
      setEditingValue(recipients[idx].value);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    addRecipientsFromText(pasted);
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addRecipientsFromText(inputValue);
      setInputValue('');
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-slate-700">Client email(s)</div>

        <div className="focus-within:ring-primary flex max-h-[440px] w-full flex-wrap gap-2 overflow-y-auto rounded-lg border p-2 focus-within:ring-2">
          <span
            ref={spanRef}
            className="pointer-events-none absolute left-0 top-0 opacity-0"
            style={{
              position: 'absolute',
              whiteSpace: 'pre',
            }}
          >
            {inputValue || editingIndex !== null ? editingValue || ' ' : placeholder || ' '}
          </span>

          {recipients.map((r, idx) => (
            <Tooltip key={r.value + idx}>
              <TooltipTrigger>
                {editingIndex === idx ? (
                  <div className="relative flex-grow">
                    <Input
                      value={editingValue}
                      onChange={e => setEditingValue(e.target.value)}
                      onBlur={commitEditingChip}
                      style={{ width: `${inputWidth}px` }}
                      className="h-7 min-w-[200px] border-0 p-0 text-sm placeholder:text-sm focus-visible:ring-0 focus-visible:ring-transparent"
                      autoFocus
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitEditingChip();
                        }
                        if (e.key === 'Escape') {
                          setEditingIndex(null);
                          setEditingValue('');
                        }
                      }}
                    />
                  </div>
                ) : (
                  <Badge
                    variant={r.isValid ? 'secondary' : 'destructive'}
                    className="flex cursor-pointer items-center gap-1 px-2 py-1 text-xs"
                    onClick={() => {
                      setEditingIndex(idx);
                      setEditingValue(r.value);
                    }}
                    tabIndex={0}
                  >
                    {r.isValid && <Check className="h-3 w-3 text-green-500" />}
                    <span className="max-w-xs truncate">{r.value}</span>
                    <X
                      className="ml-1 h-3 w-3 cursor-pointer"
                      onClick={e => {
                        e.stopPropagation();
                        removeRecipient(r.value);
                      }}
                    />
                  </Badge>
                )}
              </TooltipTrigger>
              {!r.isValid && (
                <TooltipContent>
                  <p>Invalid email</p>
                </TooltipContent>
              )}
            </Tooltip>
          ))}

          {editingIndex === null && (
            <div className="flex-grow">
              <Input
                ref={inputRef}
                placeholder={
                  hasReachedLimit ? `Maximum ${maxRecipients} emails allowed` : placeholder
                }
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onPaste={handlePaste}
                onBlur={handleInputBlur}
                className="h-7 w-full border-0 p-0 text-sm placeholder:text-sm focus-visible:ring-0 focus-visible:ring-transparent"
                style={{ minWidth: inputWidth }}
                data-testid="clientEmail"
                disabled={hasReachedLimit}
              />
            </div>
          )}
        </div>

        {duplicateWarning && (
          <Alert className="border-yellow-50 bg-yellow-50 text-sm text-yellow-800">
            {duplicateWarning}
          </Alert>
        )}
        {hasReachedLimit && (
          <Alert className="border-yellow-50 bg-yellow-50 text-sm text-yellow-800">
            Maximum {maxRecipients} emails allowed
          </Alert>
        )}
      </div>
    </TooltipProvider>
  );
}

export type { Tag, ValidationResult };
